import { forwardRef, useCallback, useLayoutEffect, useRef } from 'react';
import { Checkbox } from 'antd';
import clsx from 'clsx';
import fitContent from './fitContent';
import styles from './MultiOption.module.css';

/**
 * @typedef {Object} MultiOptionGroupOwnProps
 * @property {boolean} [fitContent] - Same behaviour as `Option.Group`: shrinks every option's
 *   content by one shared scale (down to `minScale`), then scrolls the group horizontally as one
 *   with each letter badge pinned on the left.
 * @property {number} [minScale] - Smallest scale `fitContent` may apply. Defaults to 0.8.
 */

/** @typedef {MultiOptionGroupOwnProps & import('antd').CheckboxGroupProps} MultiOptionGroupProps */

/**
 * The checkbox-group counterpart to `Option.Group` — a thin wrapper around antd's own
 * `Checkbox.Group`, so `value` is an array of selected option values and `onChange` receives the
 * next array.
 *
 * @param {MultiOptionGroupProps} props
 */
const MultiOptionGroup = forwardRef(({ className, fitContent: fit, minScale, children, ...rest }, forwardedRef) => {
    const localRef = useRef(null);

    const setRef = useCallback(
        (node) => {
            localRef.current = node;
            if (typeof forwardedRef === 'function') forwardedRef(node);
            else if (forwardedRef) forwardedRef.current = node;
        },
        [forwardedRef],
    );

    useLayoutEffect(() => {
        if (!fit || !localRef.current) return undefined;
        return fitContent(localRef.current, { minScale });
    }, [fit, minScale, children]);

    return (
        <Checkbox.Group
            ref={setRef}
            className={clsx(styles.vecMultiOptionGroup, fit && styles.vecMultiOptionGroupFit, className)}
            {...rest}
        >
            {children}
        </Checkbox.Group>
    );
});
MultiOptionGroup.displayName = 'MultiOptionGroup';

export default MultiOptionGroup;
